import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

// data
import { aggErrors, aggErrorsSelectors } from 'app-components/flux/account-discovery';
import { institutionLoginsSelectors, institutionLoginsUtils } from 'app-components/flux/institution-logins';
import { loadState, useLoadState } from 'app-components/hooks/utils';

// mui
import ErrorIcon from '@mui/icons-material/Error';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import type { InstitutionLogin } from 'app-components/flux/institution-logins/institutionLoginsTypes';
import Dump from 'components/Dump';
import QButton from 'components/QButton';

const useStyles = makeStyles()(({ spacing, palette }) => ({
  errorContainer: {
    backgroundColor: palette.error.light,
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'row',
    margin: `${spacing(4)} ${spacing(1)} 0`,
    padding: spacing(2),
    justifyContent: 'space-between',
  },
  errorTextContainer: {
    flex: 1,
    marginRight: spacing(2),
  },
  errorSecondaryText: {
    color: palette.text.secondary,
  },
  code: {
    marginLeft: 6,
    marginTop: 3,
    color: palette.text.secondary,
  },
  errorIcon: {
    //  position: 'absolute',
    color: palette.error.main,
    padding: '0 0 0 0',
    cursor: 'default',
    //  backgroundColor: 'inherit !important',
    marginRight: 10,
  },
  errorSpinner: {
    margin: spacing(0, 4.5),
    alignSelf: 'center',
  },
  errorActionButton: {
    minWidth: 155,
    alignSelf: 'center',
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  bodyContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignSelf: 'center',
  },
}));

const InstitutionErrorBanner = ({
  institutionLogin,
  location,
  className,
}: { institutionLogin: InstitutionLogin | undefined | null; location: string; className?: string }) => {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();

  const aggLoading = useSelector(institutionLoginsSelectors.getIdsRefreshing)?.includes(institutionLogin?.id || '');
  const aggAction = useSelector((state) => aggErrorsSelectors.getAggregationAction(state, { institutionLogin }));

  const aggLoadingView = useLoadState(institutionLoginsSelectors);

  const forceShowLoadingSpinner =
    loadState?.skeletonStates?.includes(aggLoadingView) ||
    loadState?.tryAgainStates?.includes(aggLoadingView) ||
    loadState?.updateRequiredStates?.includes(aggLoadingView);

  // ===================================== Other Const Values ======================================== //
  // Not all errors (aggStatus) returned from QCS should kick off fix it flow
  const isReportableError = aggAction && institutionLoginsUtils.isErrorType(aggAction?.status);

  return isReportableError ? (
    <div className={cx(classes.errorContainer, className)}>
      <Dump obj={institutionLogin} id='login in error' />
      <div className={classes.bodyContainer}>
        <ErrorIcon className={classes.errorIcon} />
        <div className={classes.errorTextContainer}>
          <div className={classes.titleContainer}>
            <Typography variant='bodyMedium' display={'block'}>
              {aggAction?.title}
            </Typography>
            {(aggAction?.statusCode || aggAction?.code) && (
              <Typography display={'block'} variant='captionRegular' className={classes.code}>
                (Care: {aggAction.statusCode || aggAction.code})
              </Typography>
            )}
          </div>
          <Typography variant='paragraphRegular' className={classes.errorSecondaryText}>
            {aggAction?.text}
          </Typography>
        </div>
      </div>
      {(aggAction?.action || forceShowLoadingSpinner) &&
        (aggLoading || forceShowLoadingSpinner ? (
          <CircularProgress size={32} className={classes.errorSpinner} />
        ) : (
          <QButton
            id={'accounts-fix-account-button'}
            variant={'contained'}
            className={classes.errorActionButton}
            onClick={() => aggErrors.kickoffAggAction(dispatch, aggAction, institutionLogin, location)}
          >
            {aggAction.actionLabel}
          </QButton>
        ))}
    </div>
  ) : null;
};

export default InstitutionErrorBanner;
